const memory = new Map();
const pending = new Map();
const waiters = [];
let active = 0;
const LIMIT = 3;
const CHUNK = 1400;

const URDU_WORDS = new Set([
  "ko", "ke", "ki", "ka", "se", "gy", "gai", "gaya", "hai", "hain", "ho",
  "kar", "mein", "main", "par", "pe", "tak", "aur", "nahi", "nahin", "wala",
  "wali", "dekho", "suno", "mar", "paka", "wah", "khana", "k", "hum", "ham",
  "ne", "bhi", "tha", "thi", "raha", "rahi", "rahe",
]);

const URDU_ROMAN = [
  ["ھ", "h"], ["ش", "sh"], ["چ", "ch"], ["خ", "kh"], ["غ", "gh"], ["ژ", "zh"],
  ["آ", "aa"], ["ا", "a"], ["ب", "b"], ["پ", "p"], ["ت", "t"], ["ٹ", "t"],
  ["ث", "s"], ["ج", "j"], ["ح", "h"], ["د", "d"], ["ڈ", "d"], ["ذ", "z"],
  ["ر", "r"], ["ڑ", "r"], ["ز", "z"], ["س", "s"], ["ص", "s"], ["ض", "z"],
  ["ط", "t"], ["ظ", "z"], ["ع", "a"], ["ف", "f"], ["ق", "q"], ["ک", "k"],
  ["گ", "g"], ["ل", "l"], ["م", "m"], ["ن", "n"], ["ں", "n"], ["و", "o"],
  ["ہ", "h"], ["ی", "i"], ["ے", "e"], ["ئ", "i"], ["ء", ""],
];

export function cleanDisplayTitle(text) {
  const source = text == null ? "" : String(text);
  const cleaned = source
    .replace(/[_]+/g, " ")
    .replace(/\.(mp4|webm|mkv|mov|avi|m4v|mp3)$/i, "")
    .replace(/(mp4|webm|mkv|mov|avi|m4v|mp3)$/i, "")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || source.trim();
}

export function looksRomanUrdu(text) {
  const words = String(text || "")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return false;
  const hits = words.filter((word) => URDU_WORDS.has(word)).length;
  return hits > 0 && hits * 3 >= words.length;
}

export function urduToRoman(text) {
  let value = String(text || "");
  URDU_ROMAN.forEach(([from, to]) => {
    value = value.split(from).join(to);
  });
  return value.replace(/\s+/g, " ").trim();
}

export function peekRoman(text) {
  const source = text == null ? "" : String(text);
  return source ? readStored(source, "ru2") : "";
}

function readStored(text, kind) {
  const key = `${kind}:${text}`;
  return memory.has(key) ? memory.get(key) : "";
}

function writeStored(text, value, kind) {
  memory.set(`${kind}:${text}`, value);
}

function extractTranslation(payload) {
  const parts = Array.isArray(payload?.[0]) ? payload[0] : [];
  const roman = parts
    .filter(
      (part) =>
        Array.isArray(part) && part[0] == null && typeof part[2] === "string",
    )
    .map((part) => part[2])
    .join("")
    .trim();
  const script = parts
    .map((part) => (typeof part?.[0] === "string" ? part[0] : ""))
    .join("")
    .trim();
  return { roman, script };
}

function romanAcceptable(source, roman) {
  if (!roman) return false;
  if (looksRomanUrdu(source)) return true;
  const tokens = source
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 3);
  if (tokens.length === 0 || tokens.length > 3) return true;
  const normalized = roman.toLowerCase();
  return tokens.some((word) => normalized.includes(word.slice(0, 4)));
}

function splitChunks(text) {
  if (text.length <= CHUNK) return [text];
  const chunks = [];
  let start = 0;
  while (start < text.length) {
    if (text.length - start <= CHUNK) {
      chunks.push(text.slice(start));
      break;
    }
    const windowEnd = start + CHUNK;
    let cut = text.lastIndexOf("</div>", windowEnd);
    if (cut > start + 80) {
      cut += 6;
    } else {
      cut = text.lastIndexOf(". ", windowEnd);
      if (cut > start + 80) cut += 1;
      else {
        cut = text.lastIndexOf(" ", windowEnd);
        if (cut <= start + 80) cut = windowEnd;
      }
    }
    chunks.push(text.slice(start, cut));
    start = cut;
  }
  return chunks;
}

function withSlot(task) {
  return new Promise((resolve, reject) => {
    const run = () => {
      active += 1;
      task().then(resolve, reject).finally(() => {
        active -= 1;
        const next = waiters.shift();
        if (next) next();
      });
    };
    if (active < LIMIT) run();
    else waiters.push(run);
  });
}

async function fetchTranslation(text, sl, tl) {
  const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sl}&tl=${tl}&dt=t&dt=rm&q=${encodeURIComponent(text)}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error("translate failed");
  return extractTranslation(await response.json());
}

async function translateChunk(chunk) {
  const { roman, script } = await fetchTranslation(chunk, "en", "ur");
  if (romanAcceptable(chunk, roman)) return roman;
  const fallback = urduToRoman(script);
  if (fallback) return fallback;
  if (roman) return roman;
  throw new Error("empty translation");
}

export function toRomanUrdu(text) {
  const source = cleanDisplayTitle(text);
  if (!source.trim()) return Promise.resolve(source);
  const cached = readStored(source, "ru2");
  if (cached) return Promise.resolve(cached);
  if (pending.has(source)) return pending.get(source);

  const job = withSlot(async () => {
    const pieces = [];
    const chunks = splitChunks(source);
    for (let i = 0; i < chunks.length; i += 1) {
      pieces.push(await translateChunk(chunks[i]));
    }
    const roman = pieces.join("").replace(/\s+/g, " ").trim();
    writeStored(source, roman, "ru2");
    return roman;
  })
    .catch(() => source)
    .finally(() => pending.delete(source));

  pending.set(source, job);
  return job;
}

export function toEnglishTitle(text) {
  const source = cleanDisplayTitle(text);
  if (!source.trim() || !looksRomanUrdu(source)) return Promise.resolve(source);
  const cached = readStored(source, "en2");
  if (cached) return Promise.resolve(cached);
  const key = `en:${source}`;
  if (pending.has(key)) return pending.get(key);

  const job = withSlot(async () => {
    const { script } = await fetchTranslation(source, "auto", "ur");
    const { script: english } = await fetchTranslation(script || source, "ur", "en");
    const result = (english || source).replace(/\s+/g, " ").trim();
    writeStored(source, result, "en2");
    return result;
  })
    .catch(() => source)
    .finally(() => pending.delete(key));

  pending.set(key, job);
  return job;
}
